import React, { useEffect, useState } from "react";
import { Step, Stepper, toast } from "@picklebet/ui";
import { Details } from "./Details";
import { Address } from "./Address";
import { Identity } from "./Identity";
import * as Session from "../../services/session";
import { useAuthStore, type CustomerLicence } from "../../stores/authStore";
import omit from "lodash/omit";
import { useShellStore } from "@/features/shell/stores/shellStore";
import { telemetry } from "@/utilities/telemetry";
import { Errors } from "../Errors";
import localObject from "utilities/localObject";
import { ATTRIBUTION_KEY, REFERRAL_KEY } from "appConfig";
import { BET_STOP_MODAL_ID_SIGNUP } from "../BetStopModalSignup";
import { getDeviceId } from "utilities/getDeviceId";
import { useSearchParams } from "@/hooks/useSearchParams";
import { signUpParams } from "@/features/auth/models/signUpParams";
import { useOtpFlow } from "@/features/auth/hooks/useOtpFlow";
import { useIsPhoneVerificationEnabled } from "@/features/auth/hooks/useIsPhoneVerificationEnabled";

export type StepName = "Details" | "Address" | "Identity" | "Verification";

type SignUpFormProps = {
  onSubmit?: () => void;
  currentStep: StepName;
  setCurrentStep: (step: StepName) => void;
};

type DetailsFormValues = {
  countryOfResidence: string;
  nationality?: string;
  email: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  dateOfBirth: string;
};

export type AddressFormValues = {
  country: string;
  postCode: string;
  state?: string;
  suburb: string;
  street: string;
  streetNumber: string;
  unit?: string;
};

export type StoredFormValues = {
  details: DetailsFormValues;
  phone: string;
  phoneVerificationRequestId: string | null;
  address: AddressFormValues;
  licence: CustomerLicence | null;
  promotionCode?: string;
};

type SignUpError = {
  message: string;
  type: string;
} | null;

const initialAddress = {
  country: "",
  postCode: "",
  state: "",
  suburb: "",
  street: "",
  streetNumber: "",
};

const SignUpForm = ({
  onSubmit,
  currentStep,
  setCurrentStep,
}: SignUpFormProps) => {
  const setZendeskTemp = useAuthStore((state) => state.setZendeskTemp);
  const setModal = useShellStore((state) => state.setModal);
  const { searchParams, removeSearchParam } = useSearchParams(signUpParams);

  const { isEnabled: isFFEnabled } = useIsPhoneVerificationEnabled();
  const { store } = useOtpFlow("signup");
  const status = store((s) => s.status);
  const session = store((s) => s.session);

  const [storedFormValues, setStoredFormValues] = useState<StoredFormValues>({
    details: {
      countryOfResidence: "",
      email: "",
      firstName: "",
      lastName: "",
      middleName: "",
      dateOfBirth: "",
    },
    phone: "",
    phoneVerificationRequestId: null,
    address: initialAddress,
    licence: null,
    promotionCode: "",
  });
  const [signUpError, setSignUpError] = useState<SignUpError>(null);
  const [storedReferralCode] = localObject.getValue<string>(REFERRAL_KEY);

  const promotionCode =
    typeof window === "undefined"
      ? undefined
      : storedReferralCode ?? searchParams.r;

  useEffect(() => {
    if (searchParams.source === "iam") {
      telemetry.signUpClicked({ component: "braze_iam" });

      removeSearchParam("source");
    }
  }, [searchParams.source, removeSearchParam]);

  useEffect(() => {
    return () => {
      // reset the temp values when component unmounts
      setZendeskTemp({ zendeskDomain: "", zendeskKey: "" });
    };
  }, [setZendeskTemp]);

  return (
    <Stepper activeStep={currentStep} hideLabel>
      <Step name={"Details"}>
        <Details
          initialValues={storedFormValues.details}
          onComplete={(details: DetailsFormValues, licence) => {
            const country = details.countryOfResidence;
            const isEnabled = isFFEnabled && country === "AU";
            const countryChanged =
              country !== storedFormValues.details.countryOfResidence;
            // when users edit a mobile field after verified
            const isReverificationNeeded = isEnabled && status === "idle";

            setStoredFormValues((prev) => {
              // wipe the phone data
              if (countryChanged || isReverificationNeeded) {
                return {
                  ...prev,
                  details,
                  licence,
                  // only reset address if the country changed
                  address: countryChanged ? initialAddress : prev.address,
                  phone: "",
                  phoneVerificationRequestId: null,
                };
              }

              if (isEnabled && session) {
                return {
                  ...prev,
                  details,
                  licence,
                  phone: session.destination,
                  phoneVerificationRequestId: session.verificationRequestId,
                };
              }

              return { ...prev, details, licence };
            });

            telemetry.completedDetails();
            setCurrentStep("Address");
          }}
        />
      </Step>
      <Step name={"Address"}>
        <Address
          storedFormValues={storedFormValues}
          onAddressSelected={(selectedAddress) => {
            setStoredFormValues((prev) => ({
              ...prev,
              address: selectedAddress,
            }));
          }}
          onComplete={(values, attributes) => {
            const { phone, phoneVerificationRequestId, ...address } = values;
            setStoredFormValues((prev) => ({
              ...prev,
              phone,
              phoneVerificationRequestId,
              address,
            }));
            if (attributes?.zendeskDomain && attributes?.zendeskKey) {
              // use temp values sent from the validations request
              setZendeskTemp({
                zendeskDomain: attributes?.zendeskDomain,
                zendeskKey: attributes?.zendeskKey,
              });
            }
            telemetry.completedAddress();
            setCurrentStep("Identity");
          }}
          onNewAddressClick={() => {
            setStoredFormValues((prev) => ({
              ...prev,
              address: initialAddress,
            }));
          }}
        />
      </Step>
      <Step name={"Identity"}>
        {signUpError && (
          <Errors type={signUpError.type} message={signUpError.message} />
        )}
        <Identity
          promotionCode={promotionCode}
          storedFormValues={storedFormValues}
          onInvalidMobileVerification={(promotionCode?: string) => {
            setStoredFormValues((prev) => ({
              ...prev,
              promotionCode,
            }));
            setCurrentStep("Address");
            toast({
              description:
                "Looks like your session timed out. Please re-enter your details to continue.",
              variant: "danger",
            });
          }}
          onComplete={async (identityFormValues) => {
            onSubmit?.();

            // Read attribution values if they exist
            let [attribution] = localObject.getValue<Record<string, unknown>>(
              ATTRIBUTION_KEY,
              {
                nominated_source: "unspecified",
              },
            );

            // Ensure attribution has default values if local storage is empty or lacks nominated_source
            attribution = {
              nominated_source: "unspecified",
              ...(attribution ?? {}),
            };

            // update with the defaults
            localObject.setValue(ATTRIBUTION_KEY, attribution);

            const {
              details,
              address,
              phone,
              phoneVerificationRequestId,
              licence,
            } = storedFormValues;

            const profile = {
              ...omit(details, ["email", "countryOfResidence"]),
              ...address,
              ...omit(identityFormValues, ["password"]),
              phone,
              phoneVerificationRequestId,
              attribution,
              ...(licence && { licence }),
            };

            // Combine identity form with the rest of the form values
            const result = await Session.signUp({
              email: details.email,
              password: identityFormValues.password,
              profile,
              deviceId: getDeviceId(),
            });

            let isExcludedPersonsMatch = false;

            if (!result.ok) {
              const { messageTemplate, message } = result.error;

              // prioritise the external NSER match to excluded persons register
              if (messageTemplate?.includes("nserMatch")) {
                setModal(BET_STOP_MODAL_ID_SIGNUP);
              }

              if (messageTemplate?.includes("nser-error")) {
                return { showRetryBetStop: true };
              }

              if (
                messageTemplate?.includes("{excluded.persons.registry.match}")
              ) {
                isExcludedPersonsMatch = true;
              }

              setSignUpError({
                message: message ?? "An error occurred",
                type: messageTemplate ?? "unknown",
              });
            }

            return { isExcludedPersonsMatch };
          }}
        />
      </Step>
    </Stepper>
  );
};

export { SignUpForm };
